import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { CalendarIcon, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { Checkbox } from './ui/checkbox';
import { submitBooking, buildWhatsAppLink } from '../mock';
import { toast } from 'sonner';

export const BookingForm = ({ language }) => {
  const [date, setDate] = useState();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sendWhatsApp, setSendWhatsApp] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    passengers: '',
    pickup: '',
    destination: '',
    notes: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const bookingData = { ...formData, date: date ? format(date, 'PPP') : '' };
      const result = await submitBooking(bookingData);
      
      if (result.success) {
        toast.success(
          language === 'en' 
            ? `Booking confirmed! Reference: ${result.bookingId}` 
            : `Reserva confirmada! Referência: ${result.bookingId}`
        );
        // Optionally also open WhatsApp pre-filled (only if the customer opted in)
        if (sendWhatsApp) {
          window.open(buildWhatsAppLink(bookingData, result.bookingId), '_blank');
        }
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          service: '',
          passengers: '',
          pickup: '',
          destination: '',
          notes: ''
        });
        setDate(undefined);
        setSendWhatsApp(false);
      }
    } catch (error) {
      toast.error(
        language === 'en' 
          ? 'Booking failed. Please try again.' 
          : 'Falha na reserva. Por favor, tente novamente.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitLabel = isSubmitting
    ? null
    : (language === 'en' ? 'Confirm Booking' : 'Confirmar Reserva');
  const processingLabel = language === 'en' ? 'Processing...' : 'Processando...';

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="booking-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Name */}
        <div>
          <Label htmlFor="name">{language === 'en' ? 'Full Name' : 'Nome Completo'} *</Label>
          <Input
            id="name"
            name="name"
            data-testid="booking-name"
            value={formData.name}
            onChange={handleChange}
            required
            placeholder={language === 'en' ? 'John Doe' : 'João Silva'}
            className="mt-1"
          />
        </div>

        {/* Email */}
        <div>
          <Label htmlFor="email">{language === 'en' ? 'Email' : 'E-mail'} *</Label>
          <Input
            id="email"
            name="email"
            type="email"
            data-testid="booking-email"
            value={formData.email}
            onChange={handleChange}
            required
            placeholder="john@example.com"
            className="mt-1"
          />
        </div>

        {/* Phone */}
        <div>
          <Label htmlFor="phone">{language === 'en' ? 'Phone' : 'Telefone'} *</Label>
          <Input
            id="phone"
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleChange}
            required
            placeholder="+351 123 456 789"
            className="mt-1"
          />
        </div>

        {/* Service Type */}
        <div>
          <Label htmlFor="service">{language === 'en' ? 'Service Type' : 'Tipo de Serviço'} *</Label>
          <Select value={formData.service} onValueChange={(value) => setFormData({ ...formData, service: value })} required>
            <SelectTrigger className="mt-1">
              <SelectValue placeholder={language === 'en' ? 'Select service' : 'Selecione serviço'} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="airport-transfer">{language === 'en' ? 'Airport Transfer' : 'Transfer Aeroporto'}</SelectItem>
              <SelectItem value="city-tour">{language === 'en' ? 'City Tour' : 'Tour Cidade'}</SelectItem>
              <SelectItem value="private-tour">{language === 'en' ? 'Private Tour' : 'Tour Privado'}</SelectItem>
              <SelectItem value="day-trip">{language === 'en' ? 'Day Trip' : 'Viagem Um Dia'}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Number of Passengers */}
        <div>
          <Label htmlFor="passengers">{language === 'en' ? 'Passengers' : 'Passageiros'} *</Label>
          <Select value={formData.passengers} onValueChange={(value) => setFormData({ ...formData, passengers: value })} required>
            <SelectTrigger className="mt-1">
              <SelectValue placeholder={language === 'en' ? 'Select number' : 'Selecione número'} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">1</SelectItem>
              <SelectItem value="2">2</SelectItem>
              <SelectItem value="3">3</SelectItem>
              <SelectItem value="4">4</SelectItem>
              <SelectItem value="5-7">5-7</SelectItem>
              <SelectItem value="8+">8+</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Date */}
        <div>
          <Label>{language === 'en' ? 'Date' : 'Data'} *</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="w-full mt-1 justify-start text-left font-normal"
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {date ? format(date, 'PPP') : <span>{language === 'en' ? 'Pick a date' : 'Escolha data'}</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={date}
                onSelect={setDate}
                initialFocus
                disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Pickup Location */}
        <div>
          <Label htmlFor="pickup">{language === 'en' ? 'Pickup Location' : 'Local Recolha'} *</Label>
          <Input
            id="pickup"
            name="pickup"
            value={formData.pickup}
            onChange={handleChange}
            required
            placeholder={language === 'en' ? 'Hotel address or location' : 'Endereço hotel ou local'}
            className="mt-1"
          />
        </div>

        {/* Destination */}
        <div>
          <Label htmlFor="destination">{language === 'en' ? 'Destination' : 'Destino'}</Label>
          <Input
            id="destination"
            name="destination"
            value={formData.destination}
            onChange={handleChange}
            placeholder={language === 'en' ? 'Optional' : 'Opcional'}
            className="mt-1"
          />
        </div>
      </div>

      {/* Additional Notes */}
      <div>
        <Label htmlFor="notes">{language === 'en' ? 'Additional Notes' : 'Notas Adicionais'}</Label>
        <Textarea
          id="notes"
          name="notes"
          value={formData.notes}
          onChange={handleChange}
          placeholder={language === 'en' ? 'Any special requests or information...' : 'Pedidos especiais ou informações...'}
          className="mt-1 min-h-[100px]"
        />
      </div>

      <div className="flex items-center gap-2">
        <Checkbox id="bf-whatsapp" data-testid="booking-whatsapp" checked={sendWhatsApp} onCheckedChange={(v) => setSendWhatsApp(!!v)} />
        <Label htmlFor="bf-whatsapp" className="text-sm font-light text-[#475569] cursor-pointer">
          {language === 'en' ? 'Also send my booking via WhatsApp' : 'Enviar também a minha reserva por WhatsApp'}
        </Label>
      </div>

      <Button
        type="submit"
        disabled={isSubmitting}
        data-testid="booking-submit-button"
        className="w-full bg-[#D4AF37] hover:bg-[#B48E2D] text-white rounded-sm py-6 text-base font-medium transition-colors duration-300 shadow-sm"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            {processingLabel}
          </>
        ) : (
          submitLabel
        )}
      </Button>
    </form>
  );
};